import {
  type EndpointResponse,
  type PredicateType,
  type ResponseBodyEvaluation,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const AMAZON_AWS_DOMAIN = 'amazonaws'

export const awsStatusCheckIntegration: TeamExternalIntegration = {
  authUrl: '',
  headers: [],
  isPrivateNetworkIntegration: false,
  name: 'AWS',
  sortIndex: 10,
  type: IntegrationType.AWS,
  url: 'https://monitoring.us-east-1.amazonaws.com/?Action=DescribeAlarms&Version=2010-08-01'
}

export const awsIamArnRegex = new RegExp(
  '^arn:(aws|aws-us-gov|aws-cn|aws-iso):iam::(\\d{12}):(role|user)+\\/([a-zA-Z0-9_.-\\/]+)?'
)

export const awsManagedPolicyArnRegex = new RegExp(
  '^arn:(aws|aws-us-gov|aws-cn|aws-iso):iam::aws:(policy)+\\/([a-zA-Z0-9_.-\\/]+)?'
)

export const awsElbArnRegex = new RegExp(
  '^arn:(aws|aws-us-gov|aws-cn|aws-iso):elasticloadbalancing:([a-zA-Z0-9_.-]+):(\\d{12}):(loadbalancer)\\/([a-zA-Z0-9_.-\\/]+)?'
)

export const awsAuthUrlRegex = new RegExp(
  `^https:\\/\\/(.+)\\.${AMAZON_AWS_DOMAIN}\\.com\\/(.+)?`
)

export const awsLoadBalancerArnRegex = new RegExp(
  '^arn:(aws|aws-us-gov|aws-cn|aws-iso):elasticloadbalancing:([a-zA-Z0-9_.-]+):(\\d{12}):(loadbalancer)\\/([\\wd-]*\\/?[\\wd-]*)?'
)

export const awsAutoscalingGroupArnRegex = new RegExp(
  '^arn:(aws|aws-us-gov|aws-cn|aws-iso):autoscaling:([a-zA-Z0-9_.-]+):(\\d{12}):(autoScalingGroup):([a-zA-Z0-9_.-]+):autoScalingGroupName\\/([\\wd-]*\\/?[\\wd-]*)?'
)

/**
 * - Supports any AWS partition
 * - Allows any service
 * - Region may be empty
 * - Requires a 12-digit account ID
 * - Accepts any non-empty resource section (':' or '/')
 *
 * Validation only checks general ARN structure.
 * Service-specific rules are enforced in backend validation.
 */
export const arnRegex =
  /^arn:aws(-[a-z0-9-]+)?:[a-z0-9-]+:[a-z0-9-]*:\d{12}:.+$/

export const awsResponseBodyEvaluationConfiguration: ResponseBodyEvaluation = {
  op: 'AND',
  predicates: [
    {
      jpQuery:
        '.DescribeAlarmsResponse.DescribeAlarmsResult.MetricAlarms[0].StateValue',
      type: 'String' as PredicateType,
      comparator: 'EQU',
      rValue: 'OK'
    }
  ]
}

export const cloudwatchAlarmRegex = new RegExp(
  '^https:\\/\\/([a-zA-Z]*-[a-zA-Z]*-\\d).console.aws.amazon.com\\/cloudwatch\\/home\\?region=([a-zA-Z]*-[a-zA-Z]*-\\d)#alarmsV2:alarm\\/([^?]+)'
)

export function convertToAWSApiUrl(url: string): string {
  if (cloudwatchAlarmRegex.test(url)) {
    const region = cloudwatchAlarmRegex.exec(url)?.[1]
    const alarmNameInUrl = cloudwatchAlarmRegex.exec(url)?.[3]

    /*
     * if a cloudwatch alarm name includes spaces, aws will use a "+" to represent them in the dashboard url
     * however, it appears that their api does not honor this
     * so we need to replace any "+"'s in the alarm name back with spaces when we convert the dashboard url to api url
     */
    const alarmName = alarmNameInUrl?.includes('+')
      ? alarmNameInUrl?.replace(new RegExp(/\+/g), '%20')
      : alarmNameInUrl

    return `https://monitoring.${region}.${AMAZON_AWS_DOMAIN}.com/?Action=DescribeAlarms&AlarmNamePrefix=${alarmName}&Version=2010-08-01`
  }
  return url
}

export function getAWSMonitorName(
  endpointResponse: EndpointResponse
): string | undefined {
  if (endpointResponse?.contentType.includes('json')) {
    return JSON.parse(endpointResponse?.responseBody)?.DescribeAlarmsResponse
      ?.DescribeAlarmsResult?.MetricAlarms[0]?.AlarmName
  }
}

export function getAWSAccountIdFromIamArn(arn: string): string | undefined {
  return awsIamArnRegex.exec(arn)?.[2]
}

export function getAWSPolicyNameFromManagedPolicyArn(
  arn: string
): string | undefined {
  return awsManagedPolicyArnRegex.exec(arn)?.[3]
}

export function getAWSAccountIdFromElbArn(arn: string): string | undefined {
  return awsElbArnRegex.exec(arn)?.[3]
}

export function getAWSLoadBalancerNameFromLoadBalancerArn(
  arn: string
): string | undefined {
  return awsLoadBalancerArnRegex.exec(arn)?.[5]
}

export function getAWSAccountIdFromASGArn(arn: string): string | undefined {
  return awsAutoscalingGroupArnRegex.exec(arn)?.[3]
}

export function getAWSAutoscalingGroupNameFromASGArn(
  arn: string
): string | undefined {
  return awsAutoscalingGroupArnRegex.exec(arn)?.[6]
}
